import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild}    from '@angular/core';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {PagerBuilder}                                                 from './configs/pager-builder';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  NFertilizationPlansGridBuilderService
}                                                                     from './configs/n-fertilization-plans-grid-builder.service';
import {
  NFertilizationStore
}                                                                     from '../../stores/n-fertilization/n-fertilization.store';
import {filter, map}                                                  from 'rxjs/operators';
import {
  FertilizerStore
}                                                                     from '../../stores/base-data/fertilizer.store';
import {
  CropTypeStore
}                                                                     from '../../stores/base-data/crop.types.store';
import {FieldStore}                                                   from '../../stores/farm/field.store';
import {
  CampaignYearStore
}                                                                     from '../../stores/login/campaignyear.store';
import {
  ApUtilService
}                                                                     from '../../ap-utils/service/ap-util.service';
import {
  FormStore
}                                                                     from '../../stores/layout/form.store';
import {
  RouterStore
}                                                                     from '../../stores/router/router.store';
import {
  ApNFertilizationBookingComponent as ApNFertilizationEntryBookingComponent
}                                                                     from '../../entry-components/ap-n-fertilization-entry-components/ap-n-fertilization-booking.component';
import {
  CropApplicationStore
}                                                                     from '../../stores/task-management/crop-application.store';
import {
  ApNFertilizationCommonService
}                                                                     from '../services/ap-n-fertilization.common.service';
import {
  ApTooltipContentService
}                                                                     from '../../ap-utils/service/ap-tooltip-content.service';
import {
  ApNFertilizationShowComponent
}                                                                     from '../../entry-components/ap-n-fertilization-entry-components/ap-n-fertilization-show.component';
import {
  FertilizerService
}                                                                     from '../../services/data/fertilizer.service';
import {
  NFertilizationService
}                                                                     from '../../services/data/n-fertilization.service';
import {
  INFertilizationBookingGridItem,
  PlanBookNApplicationRateWithArea
}                                                                     from '../types/ap-n-fertilization-booking-types';
import {
  ApNFertilizationComponentService
}                                                                     from '../services/ap-n-fertilization-component.service';
import {MapStore}                                                     from '../../stores/map/map.store';
import {
  ApShowPointsOnMapService
}                                                                     from '../../ap-utils/service/ap-show-points-on-map.service';
import {
  CropTypeNames,
  ISummaryWithPlanBooks
}                                                                     from '../types/n-fertilization-common.types';
import {
  ApNFertilizationShowComponentData
}                                                                     from '../../entry-components/ap-n-fertilization-entry-components/ap-n-fertilization-show-configs/ap-n-fertilization-show-types';
import INFertilizationPlanBook = Data.TaskManagement.INFertilizationPlanBook;

@Component({
  selector: 'ap-n-fertilization-booking',
  template: `
    <ap-dyncomponent [caption]="'Global__Booking'"
                     [selectable]="{checkboxOnly: false, mode: 'single'}"
                     [loading$]="loading$"
                     [items]="items$ | async"
                     [headerIcon]="'ap-icon-n-fertilization'"
                     [columns]="columns"
                     [pager]="pager"
                     [canSearch]="true">
    </ap-dyncomponent>
  `,
  styles: []
})
export class ApNFertilizationBookingComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public loading$: Observable<boolean> = of(true);
  public items$ = new BehaviorSubject<INFertilizationBookingGridItem[]>([]);
  public pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = [];

  constructor(private mapStore: MapStore,
              private campaignYearStore: CampaignYearStore,
              private cropApplicationStore: CropApplicationStore,
              private cropTypeStore: CropTypeStore,
              private fertilizerStore: FertilizerStore,
              private formStore: FormStore,
              private fieldStore: FieldStore,
              private gridBuilder: NFertilizationPlansGridBuilderService,
              private nFertilizationStore: NFertilizationStore,
              private routerStore: RouterStore,
              private nCommonService: ApNFertilizationCommonService,
              private tooltipContentService: ApTooltipContentService,
              private fertilizerService: FertilizerService,
              private showPointsOnMap: ApShowPointsOnMapService,
              private nFertilizationComponentService: ApNFertilizationComponentService,
              private nFertilizationService: NFertilizationService) {
  }

  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.nFertilizationStore.Listen(s => s.planBooksLoading),
      this.cropTypeStore.Listen(s => s.loading),
      this.fieldStore.Listen(s => s.loading),
      this.cropApplicationStore.Listen(s => s.loading)
    ]).pipe(
      map(statuses => statuses.some(x => x))
    );
    this.pager = this._getPager();
    this.columns = this._getColumns();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => {
      this.nFertilizationStore.LoadNFertilizationPlanningSummaries();
    }));
    this._subscriptions.push(
      combineLatest([
        this.nFertilizationStore.Bookings$(),
        this.cropTypeStore.Listen(s => s.cropTypesNames),
        this.fieldStore.Listen(s => s.data)
      ]).pipe(
        filter(([planBooks, cropTypesNames, fields]) => !!planBooks && !!cropTypesNames && !!fields),
        map(([planBooks, cropTypesNames]) => this._getGridItems(planBooks, cropTypesNames)),
      ).subscribe((items) => this.items$.next(items))
    );
    this._subscriptions.push(this.dynComponent.SelectedItems$.subscribe((items: INFertilizationBookingGridItem[]): void => {
      this.showPointsOnMap.clear();
      const applications = items.map(x => x.Applications).flat();
      const fieldIds = applications.map(x => x.FieldId);
      this.fieldStore.changeSelectedField(fieldIds.map(x => x.toString()));
      const filteredApplications = applications.filter(x => x.CropKey);
      if (filteredApplications.length > 0) {
        const max = filteredApplications.map((a) => a.Date.valueOf()).Max();
        const maxItem = filteredApplications.Find((application) => application.Date.valueOf() === max);
        this.showPointsOnMap.showAllRaster(filteredApplications, 'nd');
        this.mapStore.Legends.nLogInterpolationLegend.updateValues(maxItem.CropKey, maxItem.EcValue);
      }
    }));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this.fieldStore.changeSelectedField([]);
    this.showPointsOnMap.clear();
    this.mapStore.Layers.Clear();
    this.mapStore.Legends.removeLegend(this.mapStore.Legends.nLogInterpolationLegend);
    this.mapStore.Legends.removeLegend(this.mapStore.Legends.NPlanningLegend);
  }

  private _getGridItems(planBooks: INFertilizationPlanBook[], cropTypesNames: CropTypeNames): INFertilizationBookingGridItem[] {
    const summaries = this.nFertilizationComponentService.getSummaryWithPlanBooks(planBooks, true);
    return summaries.filter(x => x.FilteredPlanBooks && x.FilteredPlanBooks.length > 0)
      .map((s: ISummaryWithPlanBooks) => {
        const operationMode = s.Summary?.OperationMode;
        const cropId = this.nFertilizationComponentService.getCropId(s.FilteredPlanBooks) ?? '';
        const productId = this.nFertilizationComponentService.getProductId(s.FilteredPlanBooks);
        const firstPlanBook = s.FilteredPlanBooks[0];
        const fertilizer = this.fertilizerStore.getFertilizer(firstPlanBook?.ProductId);
        const nApplications = this._getNApplicationsWithArea(s.FilteredPlanBooks);
        const totalArea = nApplications.reduce((prev, current) => prev + current.AreaHa, 0);
        const totalAvgOnAreas = nApplications.reduce((prev, current) => prev + current.Avg * current.AreaHa, 0);
        const planBookApplicationDates = this.nFertilizationComponentService.getApplicationDatesData(s.FilteredPlanBooks);
        const applicationRate = this.nFertilizationComponentService.getApplicationRateFromPlanBooks(s.FilteredPlanBooks);
        return {
          Id: s.Summary?.Id,
          Summary: s.Summary,
          Status: this.nFertilizationComponentService.getStatus(s),
          FieldIds: s.FilteredPlanBooks.map((pb) => pb.FieldId),
          Count: this.nFertilizationComponentService.getPlanBooksFieldsCount(s.TotalPlanBooks, true),
          Crop: cropTypesNames[+cropId],
          CropId: cropId,
          Appl: this.nFertilizationService.getApplicationRateNameByApplication(applicationRate),
          ApplSum: this.nFertilizationService.calculateProductAmountFromPlanBooks(s.FilteredPlanBooks),
          Area: this.nFertilizationComponentService.getArea(s.FilteredPlanBooks),
          OperationMode: this.nCommonService.getOperationMode(operationMode),
          OperationModeKey: operationMode?.Key ?? '',
          Product: this.fertilizerService.getFertilizerProductName(fertilizer),
          ProductId: productId,
          ProductUnit: this.nCommonService.getProductUnit(firstPlanBook),
          ProductTooltip: this.tooltipContentService.getProductContentInfoTooltip(firstPlanBook?.ProductId),
          Avg: totalAvgOnAreas / totalArea,
          Min: nApplications.reduce((prev, current) => prev < current.Min ? current.Min : prev, 0),
          Max: nApplications.reduce((prev, current) => prev > current.Max ? prev : current.Max, 0),
          ApplicationDate: planBookApplicationDates.NewestDate,
          ApplicationDateTooltip: planBookApplicationDates.Tooltip,
          ApplicationDateItalic: planBookApplicationDates.IsMoreThanOneUniqDate,
          Note: s.Summary.Title,
          PlanBooks: s.FilteredPlanBooks,
          FieldsTooltip: this.tooltipContentService.getFieldsCropsTooltip(s.FilteredPlanBooks),
          Applications: nApplications
        } as INFertilizationBookingGridItem;
      });
  }

  private _getColumns(): ApDynGridColumnConfigBase[] {
    return this.gridBuilder.planningBookingGrid([
      {
        text: 'Global__Show',
        callback: ApUtilService.createEventEmitter(this._onShow.bind(this), this._subscriptions)
      },
      {
        text: 'Global__Export',
        callback: ApUtilService.createEventEmitter(this._onExport.bind(this), this._subscriptions)
      },
      {
        text: 'Global__Reset',
        callback: ApUtilService.createEventEmitter(this._onReset.bind(this), this._subscriptions)
      },
    ]);
  }

  private _getPager(): ApDynGridPagerConfigBase {
    return PagerBuilder.PlanningBookingPager([
      {
        text: 'Global__Show',
        callback: ApUtilService.createEventEmitter(this._pagerOnShow.bind(this), this._subscriptions)
      },
      {
        text: 'Global__Export',
        callback: ApUtilService.createEventEmitter(this._pagerOnExportShow.bind(this), this._subscriptions)
      },
    ]);
  }

  private _pagerOnShow(pager: any): void {
    if (!pager?.items || pager.items.length <= 0) {
      return;
    }
    this._onShow(pager.items[0]);
  }

  private _pagerOnExportShow(pager: any): void {
    if (!pager?.items || pager.items.length <= 0) {
      return;
    }
    this._onExport(pager.items[0]);
  }

  private _onShow(dataItem: INFertilizationBookingGridItem): void {
    const planBooksIds = dataItem.PlanBooks.map(x => x.Id);
    if (!planBooksIds) {
      return;
    }
    const componentData: ApNFertilizationShowComponentData = {
      PlanBookIds: planBooksIds,
      IsBooked: true
    };
    this.formStore.openForm({component: ApNFertilizationShowComponent, data: componentData});
  }

  private _onExport(dataItem: INFertilizationBookingGridItem): void {
    this.routerStore.navigate(['task-management/overview'], {
      id: dataItem.Summary.Id,
      url: 'n-fertilization/planning'
    }).then();
  }

  private _onReset(dataItem: INFertilizationBookingGridItem): void {
    this.formStore.openForm({component: ApNFertilizationEntryBookingComponent, data: {dataItem, book: false}});
  }

  private _getNApplicationsWithArea(planBooks: INFertilizationPlanBook[]): PlanBookNApplicationRateWithArea[] {
    const statistic: PlanBookNApplicationRateWithArea[] = [];
    planBooks.forEach(planBook => {
      const fieldGeom = this.fieldStore.getFieldGeomById(planBook.FieldGeomId);
      const nApplication = this.nFertilizationService.getNApplications(planBook.RasterStatistics);
      const cropType = this.cropTypeStore.getDefaultCroptype(planBook.MainCropId);
      statistic.push({
        PlanBookId: planBook.Id.toString(),
        FieldId: planBook.FieldId.toString(),
        Min: nApplication ? nApplication.Min : planBook.ApplicationMin,
        Max: nApplication ? nApplication.Max : planBook.ApplicationMax,
        Avg: nApplication ? nApplication.Average : planBook.ApplicationRate * planBook.Factor,
        AreaHa: fieldGeom?.AreaHa ?? 0,
        Date: new Date(planBook.ApplicationDate),
        CropKey: cropType?.CropKey,
        EcValue: planBook.ECValue
      });
    });
    return statistic;
  }
}
